// estimateWorkflow — the estimate state machine, declared once.
//
// Every row below was read off server/routes/estimates.js: the guard each
// handler applies to `estimate_status`, and the permission its
// requireEstimatePerm() asks for. Nothing here re-implements the rules — the
// server is still the authority and still refuses — but a screen that offers
// a button the server will answer 400 to ("Cannot send from state draft") is
// a screen that teaches people the app is broken.
//
//   submit           draft            → pending_approval   estimates.update
//   approve          draft | pending_approval → approved    estimates.approve_send
//   reject           pending_approval → draft              estimates.approve_send
//   send             approved         → sent               estimates.send
//   recall           sent             → draft              estimates.approve_send
//   accept-internal  sent             → accepted           estimates.approve_external
//   convert          accepted         → an invoice         estimates.convert
//   revise           any              → a new draft copy   estimates.create
//   archive/unarchive                                      estimates.archive
//
// NOTHING HERE TOUCHES MONEY. Totals, discounts and tax are computed
// server-side (computeFigures, discountMath) and are not this module's
// business; these are status transitions only.

export const ESTIMATE_STATUSES = ['draft', 'pending_approval', 'approved', 'sent', 'accepted', 'declined', 'expired'];

export const ESTIMATE_STATUS_LABELS = {
  draft: 'Draft',
  pending_approval: 'Waiting for approval',
  approved: 'Approved',
  sent: 'Sent to client',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

export const ESTIMATE_STATUS_TONES = {
  draft: 'neutral',
  pending_approval: 'warn',
  approved: 'sched',
  sent: 'now',
  accepted: 'ok',
  declined: 'stop',
  expired: 'stop',
};

/**
 * `from: null` means the transition does not depend on the current state.
 * `confirm` marks the ones worth asking about first — anything a client sees,
 * and anything that throws away approval.
 */
export const ESTIMATE_ACTIONS = [
  {
    key: 'submit',
    label: 'Submit for approval',
    path: (id) => `/estimates/${id}/submit`,
    from: ['draft'],
    permission: 'estimates.update',
  },
  {
    key: 'approve',
    label: 'Approve',
    path: (id) => `/estimates/${id}/approve`,
    // A draft can be approved directly, deliberately: the handler says a
    // manager who drafts and approves their own estimate should not need the
    // submit hop. Read off the guard, not assumed — this table first said
    // pending_approval only, which would have hidden a button that works.
    from: ['draft', 'pending_approval'],
    permission: 'estimates.approve_send',
  },
  {
    key: 'reject',
    label: 'Send back to draft',
    path: (id) => `/estimates/${id}/reject`,
    from: ['pending_approval'],
    permission: 'estimates.approve_send',
    // The handler reads { reason } from the body.
    prompt: { field: 'reason', label: 'Why is it going back?', required: false },
  },
  {
    key: 'send',
    label: 'Send to client',
    path: (id) => `/estimates/${id}/send`,
    from: ['approved'],
    permission: 'estimates.send',
    confirm: 'This emails the estimate to the client.',
    tone: 'primary',
  },
  {
    key: 'recall',
    label: 'Recall',
    path: (id) => `/estimates/${id}/recall`,
    from: ['sent'],
    permission: 'estimates.approve_send',
    confirm: 'The client keeps any copy they already have. This returns it to draft and it will need approving again.',
  },
  {
    key: 'accept-internal',
    label: 'Mark accepted',
    path: (id) => `/estimates/${id}/accept-internal`,
    from: ['sent'],
    permission: 'estimates.approve_external',
    confirm: 'Record that the client accepted this — use it when they said yes by phone or in person.',
  },
  {
    key: 'convert',
    label: 'Convert to invoice',
    path: (id) => `/estimates/${id}/convert`,
    from: ['accepted'],
    permission: 'estimates.convert',
    confirm: 'This creates an invoice from the accepted estimate.',
    tone: 'primary',
  },
  {
    key: 'revise',
    label: 'Revise',
    path: (id) => `/estimates/${id}/revise`,
    from: null,                       // the handler has no state guard
    permission: 'estimates.create',
    confirm: 'This copies the estimate into a new draft you can change.',
  },
];

/**
 * Does the SERVER allow this transition from this state?
 *
 * A screen may be narrower for product reasons — Revise has no server guard
 * at all but is only offered on a finished estimate, because revising a draft
 * is just editing it. It must never be WIDER, which is what this answers.
 */
export function allows(key, status) {
  const action = ESTIMATE_ACTIONS.find((a) => a.key === key);
  if (!action) return false;
  return action.from ? action.from.includes(status) : true;
}

/**
 * The actions to show for one estimate.
 *
 * @param estimate  the row, with estimate_status
 * @param can       (permission) => boolean — the caller's useCan
 */
export function actionsFor(estimate, can) {
  if (!estimate) return [];
  const status = estimate.estimate_status;
  return ESTIMATE_ACTIONS.filter((a) => {
    if (a.from && !a.from.includes(status)) return false;
    return typeof can === 'function' ? can(a.permission) : true;
  });
}

/** A sentence for a state, for the pill on the row. */
export function estimateStatusLabel(status) {
  if (!status) return ESTIMATE_STATUS_LABELS.draft;
  if (ESTIMATE_STATUS_LABELS[status]) return ESTIMATE_STATUS_LABELS[status];
  const words = String(status).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function estimateStatusTone(status) {
  return ESTIMATE_STATUS_TONES[status] || 'neutral';
}
